import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import { useTranslation } from 'react-i18next';

import { getNumberPlaces } from './utils';
import SliderInput from './SliderInput';
import { SLIDER_OPERATORS } from '../constants';
import { inputWidth, useSliderStyles, useStyles } from './styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useDelayedCall } from '../../../hooks/useDelayCall';
import useUpdateFilterValue from '../../../hooks/useUpdateFilterValue';

const RangeSlider = ({
	leftSliderSpacing,
	rightSliderSpacing,
	width,
	horizontal,
	hiddenlabel,
	constraints,
	appliedValue,
	disabled,
	label,
	longLabel,
	values,
	unit,
	cid,
	categoryId
}) => {
	const { t } = useTranslation();
	const min = constraints.minValue;
	const max = constraints.maxValue;
	const minOperator = constraints.minOperator;
	const maxOperator = constraints.maxOperator;
	const hasMinOperator = minOperator === SLIDER_OPERATORS.GT.id || minOperator === SLIDER_OPERATORS.GTE.id;
	const hasMaxOperator = maxOperator === SLIDER_OPERATORS.LT.id || maxOperator === SLIDER_OPERATORS.LTE.id;
	const spacingOrder = hasMaxOperator && leftSliderSpacing ? 1 : hasMinOperator && rightSliderSpacing ? 3 : null;

	const classes = useStyles({
		isHorizontal: horizontal && isWidthDown(width, 'md'),
		isRange: false,
		spacing: spacingOrder
	})();
	const sliderClasses = useSliderStyles();
	const [localSliderValue, setLocalSliderValue] = useState(hasMinOperator ? min : max);
	const id = label + '_slider';
	const aria = label + '-slider';
	const step = getNumberPlaces((max - min) / 10);
	const isInputInvalid = localSliderValue === '';
	const textInput = useRef();
	const { updateFilterValue } = useUpdateFilterValue();
	const [inputChanged, setInputChanged] = useState(false);
	const appliedState = useSelector(state => state.filters.appliedState);

	useEffect(() => {
		if (appliedValue !== localSliderValue) setLocalSliderValue(appliedValue);
		// eslint-disable-next-line
	}, [appliedValue]);

	const handleSliderChange = (_event, value) => {
		setLocalSliderValue(value);
	};

	const handleSliderChangeCommitted = (_event, value) => {
		updateFilterValue(cid, categoryId, value);
		setInputChanged(false);
		focusToSliderInput();
	};

	const focusToSliderInput = () => {
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		if (isMobile) {
			return;
		}
		if (textInput) {
			textInput.current.focus();
		}
	};

	const handleInputChange = event => {
		const parsedValue = event.target.value === '' ? '' : Number(event.target.value);

		setLocalSliderValue(parsedValue);
		changeOnDelay(parsedValue);
		setInputChanged(true);
	};

	const handleInputBlur = () => {
		if (inputChanged) {
			let validatedValue = validateValue(localSliderValue);
			setLocalSliderValue(validatedValue);
			updateFilterValue(cid, categoryId, validatedValue);
		}
	};

	const validateValue = validatedValue => {
		if (validatedValue < min || validatedValue === '') {
			return min;
		} else if (validatedValue > max) {
			return max;
		}
		return validatedValue;
	};

	const handleDelayChange = newValue => {
		let validatedValue;
		if (newValue !== '') {
			validatedValue = validateValue(newValue);
			updateFilterValue(cid, categoryId, validatedValue);
		}
	};

	const changeOnDelay = useDelayedCall(handleDelayChange);

	return (
		<Grid
			container
			direction={horizontal && isWidthDown(width, 'sm') ? 'row' : 'column'}
			justifyContent={horizontal ? 'space-between' : 'flex-start'}
			alignItems={horizontal ? 'flex-start' : 'center'}
			spacing={horizontal && isWidthDown(width, 'sm') ? 2 : 0}>
			{hiddenlabel || (
				<Grid item /* xs={horizontal ? 4 : 12}*/>
					{longLabel && (
						<Typography display="inline" variant="body1">
							{parse(longLabel) + '\u00a0\u00a0'}
						</Typography>
					)}

					<Typography display="inline" variant="body2" color={longLabel ? 'textSecondary' : 'textPrimary'}>
						{parse(label)}
					</Typography>
					{isInputInvalid && (
						<FormHelperText style={{ color: 'red' }}>
							{t('rangeSlider.outOfRange') + ': [' + min + ' ... ' + max + ']'}
						</FormHelperText>
					)}
				</Grid>
			)}

			<Grid
				item
				// xs={horizontal ? 8 : 12}
				container
				className={classes.container}>
				<Box
					display="flex"
					clone
					order={{
						xs: 1,
						sm: 1,
						md: horizontal ? 2 : 1,
						lg: horizontal ? 2 : 1,
						xl: horizontal ? 2 : 1
					}}>
					<Grid
						item
						xs={12}
						sm={12}
						md={horizontal ? (spacingOrder ? 6 : 8) : 12}
						lg={horizontal ? (spacingOrder ? 6 : 8) : 12}
						xl={horizontal ? (spacingOrder ? 6 : 8) : 12}
						className={classes.sliderContainer}>
						<Slider
							color={appliedState[cid]?.used ? 'primary' : 'secondary'}
							track={hasMinOperator ? 'inverted' : 'normal'}
							disabled={values.length < 2 || disabled}
							min={min}
							max={max}
							step={step}
							value={localSliderValue}
							onBlur={handleInputBlur}
							onChange={handleSliderChange}
							onChangeCommitted={handleSliderChangeCommitted}
							aria-labelledby={aria}
							classes={sliderClasses}
						/>
					</Grid>
				</Box>

				<Box
					display="flex"
					justifyContent={horizontal ? (hasMinOperator ? 'flex-start' : 'flex-end') : 'flex-start'}
					clone
					order={horizontal ? (hasMinOperator ? 1 : 3) : 2}>
					<Grid
						item
						xs={6}
						sm={6}
						md={horizontal ? (spacingOrder ? 3 : 4) : 6}
						lg={horizontal ? (spacingOrder ? 3 : 4) : 6}
						xl={horizontal ? (spacingOrder ? 3 : 4) : 6}>
						<SliderInput
							id={id + '-sliderinput'}
							disabled={values.length < 2 || disabled}
							error={isInputInvalid}
							value={localSliderValue}
							onBlur={handleInputBlur}
							onChange={handleInputChange}
							width={inputWidth}
							aria={aria}
							unit={unit}
							startAdornmentText={
								hasMinOperator ? SLIDER_OPERATORS[minOperator].symbol : SLIDER_OPERATORS[maxOperator].symbol
							}
							inputRef={textInput}
						/>
					</Grid>
				</Box>

				{horizontal && spacingOrder && (
					<Box display="flex" clone order={spacingOrder}>
						<Grid item xs={6} sm={6} md={3} lg={3} xl={3} />
					</Box>
				)}
			</Grid>
		</Grid>
	);
};

RangeSlider.propTypes = {
	width: PropTypes.string.isRequired,
	horizontal: PropTypes.bool,
	hiddenlabel: PropTypes.bool,
	constraints: PropTypes.shape({
		minOperator: PropTypes.oneOf(Object.keys(SLIDER_OPERATORS)),
		maxOperator: PropTypes.oneOf(Object.keys(SLIDER_OPERATORS)),
		minValue: PropTypes.number,
		maxValue: PropTypes.number
	}).isRequired,
	appliedValue: PropTypes.number.isRequired,
	disabled: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	// longLabel: PropTypes.string.isRequired,
	values: PropTypes.arrayOf(PropTypes.number).isRequired,
	unit: PropTypes.string
};

export default withWidth()(RangeSlider);
